'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { PERIODS, MEDIUMS, getTrendingMovements } from '@/lib/ontology'
import { getArtistInitials, getArtistColorVar } from '@/lib/artistAvatar'

// Sample market data aligned with ontology periods
const periodData = [
  { id: 'contemporary', name: 'Contemporary', dateRange: '1970+', volume: '$4.2B', lots: '124K', change: 12.4, confidence: 96, data: [20, 25, 22, 30, 28, 35, 40, 38, 45] },
  { id: 'post-war', name: 'Post-War', dateRange: '1945-70', volume: '$2.1B', lots: '67K', change: 8.2, confidence: 91, data: [40, 38, 42, 35, 38, 40, 42, 45, 48] },
  { id: 'modern', name: 'Modern', dateRange: '1880-1945', volume: '$3.8B', lots: '89K', change: 5.1, confidence: 94, data: [30, 32, 28, 35, 40, 38, 42, 45, 48] },
  { id: '19th-century', name: '19th Century', dateRange: '1800-1880', volume: '$1.2B', lots: '28K', change: 3.4, confidence: 92, data: [25, 26, 24, 27, 28, 26, 29, 30, 31] },
  { id: 'old-masters', name: 'Old Masters', dateRange: 'pre-1800', volume: '$1.4B', lots: '34K', change: -2.1, confidence: 93, data: [30, 28, 29, 27, 26, 25, 24, 23, 22] },
]

// Medium breakdown with icons
const mediumData = [
  { id: 'paintings', name: 'Paintings', volume: '$8.2B', share: 62 },
  { id: 'sculpture', name: 'Sculpture', volume: '$2.1B', share: 16 },
  { id: 'prints', name: 'Prints', volume: '$1.4B', share: 11 },
  { id: 'photography', name: 'Photography', volume: '$890M', share: 7 },
  { id: 'drawings', name: 'Drawings', volume: '$380M', share: 3 },
  { id: 'mixed-media', name: 'Mixed Media', volume: '$180M', share: 1 },
]

// Medium icons as simple geometric shapes
function MediumIcon({ medium }: { medium: string }) {
  const iconClass = "w-8 h-8 mb-2 mx-auto text-text-tertiary group-hover:text-accent-primary transition-colors"

  switch (medium) {
    case 'paintings':
      // Landscape rectangle - picture frame
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="4" y="8" width="24" height="16" rx="1" />
          <line x1="4" y1="20" x2="12" y2="14" />
          <line x1="12" y1="14" x2="18" y2="18" />
          <line x1="18" y1="18" x2="28" y2="12" />
          <circle cx="22" cy="12" r="2" />
        </svg>
      )
    case 'sculpture':
      // Two circles stacked - abstract sculpture
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="16" cy="10" r="6" />
          <ellipse cx="16" cy="24" rx="8" ry="4" />
          <line x1="16" y1="16" x2="16" y2="20" />
        </svg>
      )
    case 'prints':
      // Portrait rectangle - print/edition
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="8" y="4" width="16" height="20" rx="1" />
          <rect x="10" y="6" width="12" height="14" rx="0.5" />
          <line x1="10" y1="26" x2="22" y2="26" />
          <line x1="12" y1="28" x2="20" y2="28" />
        </svg>
      )
    case 'photography':
      // Two rectangles - polaroid style
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="6" y="6" width="14" height="16" rx="1" transform="rotate(-8 6 6)" />
          <rect x="12" y="8" width="14" height="16" rx="1" />
          <rect x="14" y="10" width="10" height="10" rx="0.5" />
          <line x1="14" y1="22" x2="24" y2="22" />
        </svg>
      )
    case 'drawings':
      // Scribble line - sketch
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M6 20 Q10 8, 16 16 T26 12" strokeLinecap="round" />
          <path d="M8 24 Q12 18, 18 22 T24 18" strokeLinecap="round" opacity="0.5" />
          <circle cx="26" cy="10" r="2" fill="currentColor" opacity="0.3" />
        </svg>
      )
    case 'mixed-media':
      // Overlapping shapes - collage effect
      return (
        <svg className={iconClass} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="6" y="10" width="12" height="12" rx="1" />
          <circle cx="22" cy="14" r="6" />
          <polygon points="16,6 20,14 12,14" />
        </svg>
      )
    default:
      return null
  }
}

const recentSales = [
  { artist: 'Jean-Michel Basquiat', country: 'US', movement: 'Neo-Expressionism', tier: 'Blue-chip', work: 'Untitled (Devil)', house: "Christie's", hammer: '$42.4M', premium: '18.2%', time: '2h ago', lotId: 'CHR-2024-1847', confidence: 99 },
  { artist: 'Yayoi Kusama', country: 'JP', movement: 'Superflat', tier: 'Blue-chip', work: 'Infinity Nets (TWHOQ)', house: "Sotheby's", hammer: '$8.9M', premium: '12.4%', time: '3h ago', lotId: 'SOT-2024-3291', confidence: 98 },
  { artist: 'Gerhard Richter', country: 'DE', movement: 'Abstract Expressionism', tier: 'Blue-chip', work: 'Abstraktes Bild (648-3)', house: 'Phillips', hammer: '$18.2M', premium: '14.1%', time: '5h ago', lotId: 'PHL-2024-0892', confidence: 97 },
  { artist: 'Amoako Boafo', country: 'GH', movement: 'African Contemporary', tier: 'Mid-market', work: 'Green Beret', house: "Christie's", hammer: '$3.4M', premium: '24.5%', time: '6h ago', lotId: 'CHR-2024-1901', confidence: 96 },
  { artist: 'Banksy', country: 'UK', movement: 'Street Art', tier: 'Blue-chip', work: 'Girl with Balloon', house: "Sotheby's", hammer: '$25M', premium: '8.9%', time: '8h ago', lotId: 'SOT-2024-3187', confidence: 99 },
  { artist: 'Claude Monet', country: 'FR', movement: 'Impressionism', tier: 'Blue-chip', work: 'Nymphéas', house: "Christie's", hammer: '$74M', premium: '12.0%', time: '1d ago', lotId: 'CHR-2024-1802', confidence: 99 },
  { artist: 'Takashi Murakami', country: 'JP', movement: 'Superflat', tier: 'Blue-chip', work: 'In the Land of the Dead', house: 'Phillips', hammer: '$15.2M', premium: '16.3%', time: '1d ago', lotId: 'PHL-2024-0845', confidence: 98 },
]

// Hot movements from ontology
const trendingMovements = getTrendingMovements()

const timeRanges = ['30 days', '12 months', 'YTD', 'All time']
const regions = ['Global', 'Americas', 'Europe', 'Asia-Pacific', 'Middle East']

function Sparkline({ data, positive, label }: { data: number[]; positive: boolean; label?: string }) {
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const width = 80
  const height = 32

  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((v - min) / range) * height
    return `${x},${y}`
  }).join(' ')

  const areaPoints = `0,${height} ${points} ${width},${height}`

  return (
    <div className="flex flex-col items-end">
      <svg width={width} height={height} className="overflow-visible">
        <polygon
          points={areaPoints}
          fill={positive ? 'var(--positive)' : 'var(--accent-primary)'}
          opacity={0.1}
        />
        <polyline
          points={points}
          fill="none"
          stroke={positive ? 'var(--positive)' : 'var(--accent-primary)'}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {label && (
        <span className="text-[10px] text-text-muted mt-1">{label}</span>
      )}
    </div>
  )
}

function ConfidenceBadge({ value }: { value: number }) {
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-page text-text-tertiary">
      {value}% confidence
    </span>
  )
}

export function MarketPulse() {
  const [view, setView] = useState<'categories' | 'sales'>('categories')
  const [timeRange, setTimeRange] = useState('12 months')
  const [region, setRegion] = useState('Global')

  return (
    <section id="market" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight mb-2">
              Market Pulse
            </h2>
            <p className="text-text-secondary">
              Real-time intelligence across global auction markets
            </p>
          </div>

          {/* Segmented control */}
          <div className="segmented-control">
            <button
              className={view === 'categories' ? 'active' : ''}
              onClick={() => setView('categories')}
            >
              Categories
            </button>
            <button
              className={view === 'sales' ? 'active' : ''}
              onClick={() => setView('sales')}
            >
              Recent Sales
            </button>
          </div>
        </div>

        {/* AI Signal - Condensed */}
        <div className="card p-4 mb-8 flex items-center gap-4 border-l-2" style={{ borderLeftColor: 'var(--accent-primary)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-primary)', color: 'white' }}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm">
              <span className="font-medium">Market Signal:</span>{' '}
              <span className="text-text-secondary">Contemporary leads +12.4% YoY. African Contemporary and Street Art showing strongest momentum—institutions are positioning ahead of Q2 sales.</span>
            </p>
          </div>
          <Link href="/get-started" className="text-xs text-accent-primary hover:underline flex-shrink-0">
            See analysis →
          </Link>
        </div>

        {/* Simple filters: Time + Region */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          {/* Time range pills */}
          {timeRanges.map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                timeRange === range
                  ? 'bg-text-primary text-white'
                  : 'bg-transparent border border-border text-text-secondary hover:border-border-hover'
              }`}
            >
              {range}
            </button>
          ))}

          <div className="w-px h-6 bg-border mx-2" />

          {/* Region pills */}
          {regions.map((r) => (
            <button
              key={r}
              onClick={() => setRegion(r)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                region === r
                  ? 'bg-accent-primary text-white'
                  : 'bg-transparent border border-border text-text-secondary hover:border-border-hover'
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {view === 'categories' ? (
            <motion.div
              key="categories"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {/* Period/Era cards - Primary Navigation */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {periodData.map((period) => (
                  <Link
                    key={period.id}
                    href={`/app/market/${period.id}`}
                    className="group card p-0 transition-all duration-300 cursor-pointer hover:shadow-lg hover:border-border-hover relative overflow-hidden"
                  >
                    {/* Era visual header - neutral light fill */}
                    <div
                      className="h-20 relative overflow-hidden"
                      style={{
                        background: 'linear-gradient(135deg, #F8F6F3 0%, #EFEBE6 100%)'
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-black/[0.03]" />
                      <div className="absolute bottom-3 left-4">
                        <span className="text-text-primary font-display text-2xl font-light">{period.name}</span>
                      </div>
                      <div className="absolute bottom-3 right-4">
                        <span className="text-text-tertiary text-sm">{period.dateRange}</span>
                      </div>
                    </div>

                    <div className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-text-tertiary text-sm">{period.lots} lots tracked</p>
                        </div>
                        <Sparkline data={period.data} positive={period.change >= 0} label="12mo trend" />
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          <span className="text-2xl font-display font-light">
                            {period.volume}
                          </span>
                          <span className="text-text-tertiary text-sm ml-2">hammer</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-sm font-medium tabular-nums"
                            style={{ color: period.change >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                          >
                            {period.change >= 0 ? '+' : ''}{period.change}%
                          </span>
                        </div>
                      </div>

                      {/* Confidence + Explore CTA - always visible */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border-subtle">
                        <ConfidenceBadge value={period.confidence} />
                        <span
                          className="text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all"
                          style={{ color: 'var(--accent-primary)' }}
                        >
                          Explore
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Medium breakdown - with icons */}
              <div className="card p-6 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-medium">By Medium</h3>
                  <span className="text-xs text-text-tertiary">Click to explore</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {mediumData.map((medium) => (
                    <Link
                      key={medium.id}
                      href={`/app/market/medium/${medium.id}`}
                      className="group text-center p-4 rounded-xl border border-border-subtle hover:border-accent-primary/50 hover:bg-page/50 transition-all"
                    >
                      <MediumIcon medium={medium.id} />
                      <p className="font-display text-lg font-light group-hover:text-accent-primary transition-colors mb-1">
                        {medium.name}
                      </p>
                      <p className="text-base font-display font-light text-text-secondary tabular-nums">{medium.volume}</p>
                      <p className="text-xs text-text-tertiary mt-1">{medium.share}% of market</p>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Trending movements */}
              <div className="border-t border-border pt-8">
                <p className="text-text-secondary text-sm mb-4">Trending movements</p>
                <div className="flex flex-wrap gap-2 mb-8">
                  {trendingMovements.map((movement) => (
                    <Link
                      key={movement.id}
                      href={`/app/market/movement/${movement.id}`}
                      className="px-4 py-2 rounded-full text-sm border border-border text-text-secondary hover:border-accent-primary hover:text-accent-primary cursor-pointer transition-all"
                    >
                      {movement.name}
                    </Link>
                  ))}
                </div>

                {/* CTAs */}
                <div className="flex flex-wrap gap-4">
                  <Link
                    href="/app/market/contemporary"
                    className="pill"
                    style={{ background: 'var(--accent-primary)', color: 'white' }}
                  >
                    Explore market intelligence
                  </Link>
                  <Link
                    href="/get-started"
                    className="pill flex items-center gap-2"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--border-standard)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Request access
                  </Link>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="sales"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <div className="card overflow-hidden mb-12">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left px-6 py-4 text-sm font-medium text-text-tertiary">Artist</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-text-tertiary">Movement</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-text-tertiary">Tier</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-text-tertiary">Work</th>
                        <th className="text-left px-6 py-4 text-sm font-medium text-text-tertiary">House</th>
                        <th className="text-right px-6 py-4 text-sm font-medium text-text-tertiary">Hammer</th>
                        <th className="text-right px-6 py-4 text-sm font-medium text-text-tertiary">Premium</th>
                        <th className="text-right px-6 py-4 text-sm font-medium text-text-tertiary">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentSales.map((sale, i) => (
                        <tr
                          key={i}
                          className="border-b border-border-subtle hover:bg-page transition-colors cursor-pointer"
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center font-medium text-white text-sm flex-shrink-0"
                                style={{ background: `var(${getArtistColorVar(sale.artist)})` }}
                              >
                                {getArtistInitials(sale.artist)}
                              </div>
                              <div>
                                <span className="font-medium">{sale.artist}</span>
                                <span className="text-xs text-text-tertiary bg-page px-1.5 py-0.5 rounded ml-2">{sale.country}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-xs px-2 py-1 rounded-full border border-border text-text-secondary">
                              {sale.movement}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              sale.tier === 'Blue-chip'
                                ? 'bg-accent-primary/10 text-accent-primary'
                                : sale.tier === 'Mid-market'
                                ? 'bg-accent-warm/10 text-accent-warm'
                                : 'bg-positive/10 text-positive'
                            }`}>
                              {sale.tier}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-text-secondary">{sale.work}</td>
                          <td className="px-6 py-4 text-text-secondary">{sale.house}</td>
                          <td className="px-6 py-4 text-right font-medium tabular-nums">{sale.hammer}</td>
                          <td className="px-6 py-4 text-right tabular-nums" style={{ color: 'var(--positive)' }}>
                            +{sale.premium}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Link
                              href="/get-started"
                              className="text-sm hover:underline"
                              style={{ color: 'var(--accent-primary)' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {sale.time} →
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Trending movements */}
              <div className="border-t border-border pt-8">
                <p className="text-text-secondary text-sm mb-4">Trending movements</p>
                <div className="flex flex-wrap gap-2 mb-8">
                  {trendingMovements.map((movement) => (
                    <Link
                      key={movement.id}
                      href={`/app/market/movement/${movement.id}`}
                      className="px-4 py-2 rounded-full text-sm border border-border text-text-secondary hover:border-accent-primary hover:text-accent-primary cursor-pointer transition-all"
                    >
                      {movement.name}
                    </Link>
                  ))}
                </div>

                {/* CTAs */}
                <div className="flex flex-wrap gap-4">
                  <Link
                    href="/app/market/contemporary"
                    className="pill"
                    style={{ background: 'var(--accent-primary)', color: 'white' }}
                  >
                    Explore market intelligence
                  </Link>
                  <Link
                    href="/get-started"
                    className="pill flex items-center gap-2"
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--border-standard)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Request access
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
}
